<template>
  <div>
    <!-- Plan Performance Overview -->
    <DashboardCard title="Plan Performance Analytics">
      <!-- Key Metrics -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="text-center p-3 bg-blue-50 rounded-lg">
          <div class="text-xl font-bold text-blue-600">
            {{ activePlansCount }}
          </div>
          <div class="text-xs text-blue-600">Active Plans</div>
          <div class="text-xs text-gray-500 mt-1">
            of {{ totalPlansAvailable }}
          </div>
        </div>

        <div class="text-center p-3 bg-green-50 rounded-lg">
          <div class="text-xl font-bold text-green-600">
            {{ planUtilization }}%
          </div>
          <div class="text-xs text-green-600">Utilization</div>
          <div class="text-xs text-gray-500 mt-1">Portfolio usage</div>
        </div>

        <div class="text-center p-3 bg-purple-50 rounded-lg">
          <div class="text-lg font-bold text-purple-600">
            {{ mostPopularPlan.displayName || "None" }}
          </div>
          <div class="text-xs text-purple-600">Top Plan</div>
          <div class="text-xs text-gray-500 mt-1">
            {{ mostPopularPlan.subscribers || 0 }} users
          </div>
        </div>

        <div class="text-center p-3 bg-orange-50 rounded-lg">
          <div class="text-xl font-bold text-orange-600">
            {{ concentrationRatio }}%
          </div>
          <div class="text-xs text-orange-600">Concentration</div>
          <div class="text-xs text-gray-500 mt-1">Market share</div>
        </div>
      </div>

      <!-- Chart and Performance Matrix Side by Side -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Plan Distribution Chart -->
        <div>
          <ChartWidget
            title="Plan Distribution"
            chart-type="donut"
            :data="planChartData"
            :show-period-selector="false"
            :show-export="false"
            :colors="[
              '#3B82F6',
              '#10B981',
              '#F59E0B',
              '#EF4444',
              '#8B5CF6',
              '#EC4899',
              '#6B7280',
              '#14B8A6'
            ]"
          >
            <template #action>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-600"
                  >Total: {{ totalTenants }}</span
                >
              </div>
            </template>
          </ChartWidget>
        </div>

        <!-- Plan Performance Matrix -->
        <div>
          <h4 class="text-sm font-medium text-gray-700 mb-3">
            Plan Performance Ranking
          </h4>
          <div class="space-y-2">
            <div
              v-for="plan in planPerformanceMatrix"
              :key="plan.name"
              class="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div class="flex items-center flex-1">
                <div class="flex-shrink-0">
                  <div
                    class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    :class="plan.performanceClass"
                  >
                    {{ plan.rank }}
                  </div>
                </div>
                <div class="ml-3 flex-1">
                  <div class="flex items-center justify-between">
                    <h5 class="text-sm font-medium text-gray-900">
                      {{ plan.displayName }}
                    </h5>
                    <span :class="plan.trendClass" class="text-xs font-medium">
                      <i class="material-icons text-xs mr-1">{{
                        plan.trendIcon
                      }}</i>
                      {{ plan.performanceLabel }}
                    </span>
                  </div>
                  <div
                    class="mt-1 flex items-center justify-between text-xs text-gray-600"
                  >
                    <span
                      >{{ plan.subscribers }} subscribers ({{
                        plan.percentage
                      }}%)</span
                    >
                    <span class="text-xs text-gray-500">{{
                      plan.marketPosition
                    }}</span>
                  </div>
                  <div class="mt-1.5 w-full bg-gray-200 rounded-full h-1">
                    <div
                      class="h-1 rounded-full transition-all duration-500"
                      :class="plan.progressClass"
                      :style="{ width: plan.percentage + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-between items-center text-xs text-gray-600">
          <span>{{ totalTenants }} total subscribers</span>
          <span
            >{{ activePlansCount }}/{{ totalPlansAvailable }} plans active</span
          >
          <span v-if="unusedPlansCount > 0" class="text-purple-600">
            {{ unusedPlansCount }} plans unused
          </span>
        </div>
      </template>
    </DashboardCard>
  </div>
</template>

<script>
import DashboardCard from "./DashboardCard.vue";
import ChartWidget from "./ChartWidget.vue";

export default {
  name: "PlanAnalyticsWidget",
  components: {
    DashboardCard,
    ChartWidget
  },
  props: {
    planData: {
      type: Object,
      required: true,
      default: () => ({
        data: [],
        frequency_stats: {},
        plan_stats: {}
      })
    }
  },
  computed: {
    totalTenants() {
      return this.planData?.frequency_stats?.total_active_tenants || 0;
    },

    totalPlansAvailable() {
      return Object.keys(this.planData?.plan_stats || {}).length;
    },

    activePlansCount() {
      const planStats = this.planData?.plan_stats || {};
      return Object.values(planStats).filter(count => count > 0).length;
    },

    unusedPlansCount() {
      return this.totalPlansAvailable - this.activePlansCount;
    },

    planUtilization() {
      return this.totalPlansAvailable > 0
        ? Math.round((this.activePlansCount / this.totalPlansAvailable) * 100)
        : 0;
    },

    mostPopularPlan() {
      const data = this.planData?.data || [];
      if (data.length === 0) {
        return { name: "", subscribers: 0, percentage: 0, displayName: "" };
      }

      const popular = data.reduce((max, plan) =>
        (plan.subscribers || 0) > (max.subscribers || 0) ? plan : max
      );

      return {
        ...popular,
        displayName: this.formatPlanName(popular.name || ""),
        percentage:
          this.totalTenants > 0
            ? Math.round(((popular.subscribers || 0) / this.totalTenants) * 100)
            : 0
      };
    },

    concentrationRatio() {
      // Percentage of total tenants in the most popular plan
      return this.mostPopularPlan.percentage || 0;
    },

    planPerformanceMatrix() {
      const data = this.planData?.data || [];

      return data
        .map((plan, index) => {
          const subscribers = plan.subscribers || 0;
          const percentage =
            this.totalTenants > 0
              ? Math.round((subscribers / this.totalTenants) * 100)
              : 0;

          return {
            ...plan,
            displayName: this.formatPlanName(plan.name || "Unknown Plan"),
            percentage,
            rank: index + 1,
            performanceClass: this.getPerformanceClass(percentage),
            performanceLabel: this.getPerformanceLabel(percentage),
            trendClass: this.getTrendClass(percentage),
            trendIcon: this.getTrendIcon(percentage),
            progressClass: this.getProgressClass(percentage),
            marketPosition: this.getMarketPosition(percentage)
          };
        })
        .sort((a, b) => (b.subscribers || 0) - (a.subscribers || 0))
        .map((plan, index) => ({ ...plan, rank: index + 1 }));
    },

    planChartData() {
      const data = this.planData?.data || [];
      return data.map(plan => ({
        name: this.formatPlanName(plan.name || "Unknown Plan"),
        y: plan.subscribers || 0
      }));
    }
  },

  methods: {
    formatPlanName(name) {
      if (!name) return "Unknown Plan";
      return name
        .split(/[-_\s]+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },

    getPerformanceClass(percentage) {
      if (percentage >= 40) return "bg-green-600";
      if (percentage >= 25) return "bg-blue-600";
      if (percentage >= 15) return "bg-yellow-600";
      return "bg-gray-600";
    },

    getPerformanceLabel(percentage) {
      if (percentage >= 40) return "Leading";
      if (percentage >= 25) return "Strong";
      if (percentage >= 15) return "Growing";
      return "Emerging";
    },

    getTrendClass(percentage) {
      if (percentage >= 40) return "text-green-600";
      if (percentage >= 25) return "text-blue-600";
      if (percentage >= 15) return "text-yellow-600";
      return "text-gray-600";
    },

    getTrendIcon(percentage) {
      if (percentage >= 40) return "trending_up";
      if (percentage >= 25) return "trending_flat";
      if (percentage >= 15) return "trending_down";
      return "trending_down";
    },

    getProgressClass(percentage) {
      if (percentage >= 40) return "bg-green-500";
      if (percentage >= 25) return "bg-blue-500";
      if (percentage >= 15) return "bg-yellow-500";
      return "bg-gray-500";
    },

    getMarketPosition(percentage) {
      if (percentage >= 40) return "Market Leader";
      if (percentage >= 25) return "Strong Player";
      if (percentage >= 15) return "Niche Player";
      return "Emerging";
    },

    calculateBalanceScore(scores, total) {
      if (total === 0 || scores.length === 0) return 0;

      // Calculate how evenly distributed the plans are
      const expectedPerPlan = total / scores.length;
      const variance =
        scores.reduce((sum, score) => {
          const diff = score - expectedPerPlan;
          return sum + diff * diff;
        }, 0) / scores.length;

      const maxVariance = (total * total) / scores.length;
      const balanceScore = Math.max(0, 100 - (variance / maxVariance) * 100);

      return Math.round(balanceScore);
    }
  }
};
</script>
